import LBox from "@components/LBox";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import { getActiveTab } from "@hooks/useActiveTab";
import { fonts } from "@theme";
import { router } from "expo-router";
import { useNavigationStore } from "@utils/navigation/useNavigationStore";
import { ReactNode, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import type { LayoutChangeEvent } from "react-native";

type StickyHeaderProps = {
  setStickyHeaderHeight: (height: number) => void;
  artistId?: string;
  featuredAccess?: ReactNode;
};

const StickyHeader = ({
  setStickyHeaderHeight,
  artistId,
  featuredAccess,
}: StickyHeaderProps) => {
  const { segments, segmentsHistory } = useNavigationStore(
    useShallow((state) => ({
      segments: state.segments,
      segmentsHistory: state.segmentsHistory,
    })),
  );

  const [experiencesActive, musicActive, communityActive] = useMemo(() => {
    const lastCurrentSegment = segments.at(-1);
    const lastSegmentHistory = segmentsHistory.at(-1)?.at(-1);

    if (lastCurrentSegment === "community") return [false, false, true];
    if (lastCurrentSegment === "experiences") return [true, false, false];
    if (lastCurrentSegment === "[artistId]") return [false, true, false];
    if (lastSegmentHistory === "community") return [false, false, true];
    if (lastSegmentHistory === "experiences") return [true, false, false];
    if (lastSegmentHistory === "[artistId]") return [false, true, false];

    return [false, false, false];
  }, [segments, segmentsHistory]);

  return (
    <LBox
      flexDirection="column"
      alignItems="flex-start"
      bg="screenBg"
      pt={featuredAccess ? "s4" : undefined}
      paddingHorizontal="s2"
    >
      {featuredAccess}

      <LBox
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        backgroundColor="screenBg"
        paddingTop="s2"
        paddingBottom="l1"
        paddingHorizontal="s2"
        width="100%"
        onLayout={(e: LayoutChangeEvent) => {
          setStickyHeaderHeight(e.nativeEvent.layout.height);
        }}
      >
        <LBox
          backgroundColor="screenBg"
          bottom={0}
          height={20}
          left={0}
          position="absolute"
          right={0}
        />
        <LBox flexDirection="row" gap="m1">
          <LPressable
            sentryEventName="artist_page_music_tab"
            onPress={() => {
              if (!artistId) return;

              // Note: If we don't use replace here, tapping back in artist screen might change the tabs between music and experiences
              router.replace({
                pathname: `/(app)/(tabs)/${getActiveTab()}/artist/[artistId]`,
                params: {
                  artistId,
                  collectionType: "artist",
                },
              });
            }}
          >
            <LBox>
              <LText
                variant="menuButton"
                fontSize={16}
                fontFamily={fonts.medium}
                lineHeight={19.2}
                color="textPrimary"
                opacity={musicActive ? 1 : 0.7}
              >
                Music
              </LText>
              <LBox
                width="100%"
                height={2}
                backgroundColor={[
                  "buttonBackgroundPrimaryLeft",
                  "buttonBackgroundPrimaryRight",
                ]}
                gradientDirection="horizontal"
                opacity={musicActive ? 1 : 0}
                position="absolute"
                bottom={-12}
                left={0}
                right={0}
              />
            </LBox>
          </LPressable>
          <LPressable
            sentryEventName="artist_page_experiences_tab"
            onPress={() => {
              if (!artistId) return;

              // Note: If we don't use replace here, tapping back in artist screen might change the tabs between music and experiences
              router.replace({
                pathname: `/(app)/(tabs)/${getActiveTab()}/artist/[artistId]/experiences`,
                params: {
                  artistId,
                  collectionType: "artist",
                },
              });
            }}
          >
            <LBox>
              <LText
                variant="menuButton"
                fontSize={16}
                fontFamily={fonts.medium}
                lineHeight={19.2}
                color="textPrimary"
                opacity={experiencesActive ? 1 : 0.7}
              >
                Access
              </LText>
              <LBox
                width="100%"
                height={2}
                backgroundColor={[
                  "buttonBackgroundPrimaryLeft",
                  "buttonBackgroundPrimaryRight",
                ]}
                gradientDirection="horizontal"
                opacity={experiencesActive ? 1 : 0}
                position="absolute"
                left={0}
                right={0}
                bottom={-12}
              />
            </LBox>
          </LPressable>
          <LPressable
            sentryEventName="artist_page_community_tab"
            onPress={() => {
              if (!artistId) return;

              router.replace({
                pathname: `/(app)/(tabs)/${getActiveTab()}/artist/[artistId]/community`,
                params: {
                  artistId,
                  collectionType: "artist",
                },
              });
            }}
          >
            <LBox>
              <LText
                variant="menuButton"
                fontSize={16}
                fontFamily={fonts.medium}
                lineHeight={19.2}
                color="textPrimary"
                opacity={communityActive ? 1 : 0.7}
              >
                Community
              </LText>
              <LBox
                width="100%"
                height={2}
                backgroundColor={[
                  "buttonBackgroundPrimaryLeft",
                  "buttonBackgroundPrimaryRight",
                ]}
                gradientDirection="horizontal"
                opacity={communityActive ? 1 : 0}
                position="absolute"
                left={0}
                right={0}
                bottom={-12}
              />
            </LBox>
          </LPressable>
        </LBox>
      </LBox>
    </LBox>
  );
};

export default StickyHeader;
